import { useMemo, useState } from "react";
import PpLogo from "../../common/PpLogo";
import VampStandingPill from "./VampStandingPill";
import VampTooltip from "./VampTooltip";
import {
  accountKey,
  accountReading,
  accountWarnings,
  caidKey,
  caidReading,
  caidWarnings,
  sortCaids,
  VAMP_LISTED_CAIDS,
  type VampMonthContext,
  type VampReading,
} from "./vampStanding";
import type { VampCaid, VampAccount } from "../../../types/vampRates";

interface VampAccountListProps {
  accounts: VampAccount[];
  ctx: VampMonthContext;
  selectedKey: string;
  onSelect: (key: string) => void;
}

const GRID = "grid grid-cols-[26px_minmax(0,1fr)_72px_132px] items-center gap-x-[11px]";

const RATE_TIP =
  "This CAID’s TC15 + TC40 events divided by its settled Visa transactions. Visa’s excessive threshold is 1.5%. The figure is greyed while the CAID is under the 1,500-event threshold, because Visa will not score it.";

const rowClass = (selected: boolean, nested: boolean) =>
  `${GRID} w-full cursor-pointer border-t border-l-[3px] border-t-gray-100 py-[9px] pr-4 text-left ${nested ? "pl-10" : "pl-4"} ${
    selected ? "border-l-brand-500 bg-brand-50" : `border-l-transparent ${nested ? "bg-gray-25" : "bg-white"}`
  }`;

// Only the account row carries a mark; its CAID rows leave the slot empty. The box is fixed
// because PpLogo's images set their own width.
//
// A CheckoutChamp row stands for the processor its enrolments name rather than for the CRM login
// underneath, so it is marked with that name's initial - the CheckoutChamp logo would name a
// company the row is not about.
function RowMark({ ppType, name }: { ppType: string; name: string }) {
  return (
    <div className="flex h-[22px] w-[26px] items-center justify-center">
      {ppType === "checkout_champ" ? (
        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-md bg-gray-100 text-[11px] font-semibold uppercase text-gray-500">
          {name.trim().charAt(0)}
        </span>
      ) : (
        <PpLogo ppType={ppType} />
      )}
    </div>
  );
}

function WarnIcon({ label }: { label: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f79009" strokeWidth="2.2" strokeLinecap="round" className="shrink-0" aria-label={label}>
      <path d="M12 9v4M12 17h.01M10.3 3.9L2.4 17a2 2 0 001.7 3h15.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
    </svg>
  );
}

function Chevron({ open, size = 13 }: { open: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d={open ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
    </svg>
  );
}

const toggled = (set: Set<string>, key: string): Set<string> => {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

interface CaidRow {
  caid: VampCaid;
  reading: VampReading;
  warnings: string[];
}

interface AccountGroup {
  account: VampAccount;
  key: string;
  reading: VampReading;
  rows: CaidRow[];
  warnings: string[];
}


export default function VampAccountList({ accounts, ctx, selectedKey, onSelect }: VampAccountListProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [drawersOpen, setDrawersOpen] = useState<Set<string>>(new Set());

  // Every row's totals feed its rate and standing, so they are derived once per
  // month rather than again on each collapse or drawer toggle.
  const groups = useMemo<AccountGroup[]>(
    () =>
      accounts.map((account) => ({
        account,
        key: accountKey(account),
        reading: accountReading(account, ctx),
        rows: sortCaids(account, ctx.month).map((caid) => ({
          caid,
          reading: caidReading(caid, ctx),
          warnings: caidWarnings(caid, ctx.month),
        })),
        warnings: accountWarnings(account, ctx.month),
      })),
    [accounts, ctx],
  );

  const renderAccount = ({ account, key, reading, warnings }: AccountGroup) => {
    const unmatched = reading.unmatched;
    const open = !collapsed.has(key);
    return (
      <div
        key={key}
        role="button"
        tabIndex={0}
        className={rowClass(selectedKey === key, false)}
        onClick={() => onSelect(key)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(key);
          }
        }}
      >
        <RowMark ppType={account.ppType} name={account.name} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold leading-5 text-gray-900">{account.name}</span>
            {warnings.length > 0 && <WarnIcon label={warnings.join(" ")} />}
          </div>
          <div className="truncate text-theme-xs text-gray-400">
            {account.caids.length} CAIDs{unmatched ? " · none matched" : ""}
          </div>
        </div>
        <VampTooltip title={RATE_TIP} placement="top">
          <span className={`whitespace-nowrap text-right text-[13px] font-semibold leading-[22px] tabular-nums ${unmatched ? "text-gray-300" : "text-gray-400"}`}>
            {reading.rateLabel}
          </span>
        </VampTooltip>
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? "Collapse account" : "Expand account"}
          className="flex h-7 w-7 items-center justify-center justify-self-end rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed(toggled(collapsed, key));
          }}
        >
          <Chevron open={open} size={16} />
        </button>
      </div>
    );
  };

  const renderCaid = ({ caid, reading, warnings }: CaidRow) => {
    const key = caidKey(caid);
    const unmatched = reading.unmatched;
    return (
      <button key={key} type="button" className={rowClass(selectedKey === key, true)} onClick={() => onSelect(key)}>
        <span />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13px] font-medium leading-5 text-gray-600">{caid.title}</span>
            {warnings.length > 0 && <WarnIcon label={warnings.join(" ")} />}
          </div>
          <div className="truncate text-theme-xs text-gray-400">
            {unmatched ? "not matched to a BIN and CAID" : `BIN ${caid.bin} · CAID ${caid.caid}`}
          </div>
        </div>
        <VampTooltip title={RATE_TIP} placement="top">
          <span className={`whitespace-nowrap text-right text-sm font-bold leading-[22px] tabular-nums ${reading.rateInk}`}>
            {reading.rateLabel}
          </span>
        </VampTooltip>
        <div className="justify-self-end">
          <VampStandingPill standing={reading.standing} />
        </div>
      </button>
    );
  };

  const renderDrawer = (key: string, rest: CaidRow[]) => {
    const open = drawersOpen.has(key);
    return (
      <button
        key={`drawer:${key}`}
        type="button"
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 border-t border-gray-100 bg-gray-25 py-2 pl-10 pr-4 text-left text-theme-xs text-gray-500"
        onClick={() => setDrawersOpen(toggled(drawersOpen, key))}
      >
        <Chevron open={open} />
        {open ? "Show less" : `Show ${rest.length} more`}
      </button>
    );
  };

  const renderGroup = (group: AccountGroup) => {
    const rendered = [renderAccount(group)];
    if (collapsed.has(group.key)) return rendered;
    const rest = group.rows.slice(VAMP_LISTED_CAIDS);
    rendered.push(...group.rows.slice(0, VAMP_LISTED_CAIDS).map(renderCaid));
    if (rest.length > 0) {
      if (drawersOpen.has(group.key)) rendered.push(...rest.map(renderCaid));
      rendered.push(renderDrawer(group.key, rest));
    }
    return rendered;
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-theme-xs font-medium uppercase tracking-[0.04em] text-gray-400">
        Accounts &amp; CAIDs
      </div>
      {groups.map(renderGroup)}
    </div>
  );
}
